import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PssPlayerDetails, PssStarSystem, PssStarSystemDetail } from '../../../services/pss-auth.service';

type GalaxyNode = {
  id: string; title: string; svgX: number; svgY: number;
  colorClass: string; ownerName: string; attackerName: string;
  cooldownClass: string; cooldownText: string; cooldownShort: string;
};

@Component({
  selector: 'app-galaxy-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './galaxy-tab.component.html',
  styleUrls: ['./galaxy-tab.component.css']
})
export class GalaxyTabComponent {
  @Input() details!: PssPlayerDetails;
  @Input() starSystems = new Map<string, PssStarSystem>();

  galaxySearch = '';
  galaxySortBy: 'id' | 'cooldown' | 'owner' = 'id';
  galaxySortAsc = true;
  galaxyView: 'list' | 'map' = 'list';

  // Pan / zoom
  galaxyMapScale   = 1;
  galaxyMapOffsetX = 0;
  galaxyMapOffsetY = 0;
  private gMapPanning       = false;
  private gMapPanStartX     = 0;
  private gMapPanStartY     = 0;
  private gMapPanBaseX      = 0;
  private gMapPanBaseY      = 0;
  private gMapDidPan        = false;
  private gMapTouchDist     = 0;
  private gMapTouchScaleBase = 1;

  // Hover tooltip
  galaxyMapHovered: GalaxyNode | null = null;
  galaxyMapTooltipX = 0;
  galaxyMapTooltipY = 0;

  // Static background stars (generated once)
  readonly galaxyBgStars: { x: number; y: number; r: number; o: number }[] =
    Array.from({ length: 90 }, () => ({
      x: Math.random() * 480,
      y: Math.random() * 480,
      r: Math.random() * 0.9 + 0.2,
      o: Math.random() * 0.5 + 0.15,
    }));

  // ── List view ────────────────────────────────────────────────────────────

  get ownedSystems(): PssStarSystemDetail[] {
    const q = this.galaxySearch.toLowerCase();
    let list = (this.details?.starSystemDetails ?? []).filter(s => s.ownerType !== 'None' && s.ownerName);
    if (q) list = list.filter(s =>
      this.systemTitle(s.starSystemId).toLowerCase().includes(q) ||
      (s.ownerName ?? '').toLowerCase().includes(q)
    );
    const dir = this.galaxySortAsc ? 1 : -1;
    return list.sort((a, b) => {
      switch (this.galaxySortBy) {
        case 'cooldown': return dir * (this.timerMs(a.engagementCooldownEndDate) - this.timerMs(b.engagementCooldownEndDate));
        case 'owner':    return dir * (a.ownerName ?? '').localeCompare(b.ownerName ?? '');
        default:         return dir * (parseInt(a.starSystemId) - parseInt(b.starSystemId));
      }
    });
  }

  setGalaxySort(col: 'id' | 'cooldown' | 'owner'): void {
    if (this.galaxySortBy === col) { this.galaxySortAsc = !this.galaxySortAsc; }
    else { this.galaxySortBy = col; this.galaxySortAsc = true; }
  }

  systemTitle(id: string): string {
    return this.starSystems.get(id)?.title ?? `System #${id}`;
  }

  cooldownClass(iso: string): string {
    if (!iso || iso.startsWith('2000-01-01')) return '';
    const diff = new Date(iso + 'Z').getTime() - Date.now();
    if (diff <= 0)       return 'timer-expired';
    if (diff < 3600000)  return 'timer-soon';
    return 'timer-active';
  }

  formatTimerDate(iso: string): string {
    if (!iso || iso.startsWith('2000-01-01') || iso.startsWith('1900-01-01')) return '—';
    const d = new Date(iso + 'Z');
    const now = Date.now();
    const diff = d.getTime() - now;
    if (Math.abs(diff) < 60000) return 'now';
    const absDiff = Math.abs(diff);
    const h = Math.floor(absDiff / 3600000);
    const m = Math.floor((absDiff % 3600000) / 60000);
    const suffix = diff > 0 ? 'remaining' : 'ago';
    if (h >= 24) { const day = Math.floor(h / 24); return `${day}d ${h % 24}h ${suffix}`; }
    return h > 0 ? `${h}h ${m}m ${suffix}` : `${m}m ${suffix}`;
  }

  private timerMs(iso: string): number {
    if (!iso || iso.startsWith('2000-01-01') || iso.startsWith('1900-01-01')) return 0;
    return new Date(iso + 'Z').getTime();
  }

  private timerShort(ms: number): string {
    if (ms <= 0) return '';
    const h = Math.floor(ms / 3600000);
    if (h >= 24) return `${Math.floor(h / 24)}d`;
    if (h >= 1)  return `${h}h`;
    return `${Math.floor(ms / 60000)}m`;
  }

  // ── Map view ─────────────────────────────────────────────────────────────

  get galaxyTransform(): string {
    return `translate(${240 + this.galaxyMapOffsetX} ${240 + this.galaxyMapOffsetY}) scale(${this.galaxyMapScale}) translate(-240 -240)`;
  }

  get galaxyMapNodes(): GalaxyNode[] {
    const systems = Array.from(this.starSystems.values());
    if (!systems.length) return [];
    const xs = systems.map(s => +s.x);
    const ys = systems.map(s => +s.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const rangeX = maxX - minX || 1;
    const rangeY = maxY - minY || 1;
    const PAD = 24, SIZE = 480;
    const detailMap = new Map<string, PssStarSystemDetail>();
    (this.details?.starSystemDetails ?? []).forEach(d => detailMap.set(d.starSystemId, d));
    const now = Date.now();
    return systems.map(s => {
      const svgX = PAD + ((+s.x - minX) / rangeX) * (SIZE - 2 * PAD);
      const svgY = PAD + ((+s.y - minY) / rangeY) * (SIZE - 2 * PAD);
      const detail = detailMap.get(s.id);
      let colorClass = 'dot-unclaimed';
      let ownerName = '';
      let attackerName = '';
      let cooldownClass = '';
      let cooldownText = '—';
      let cooldownShort = '';
      if (detail && detail.ownerType !== 'None') {
        ownerName = detail.ownerName ?? '';
        attackerName = (detail.attackerType && detail.attackerType !== 'None') ? (detail.attackerName ?? '') : '';
        const cdMs = this.timerMs(detail.engagementCooldownEndDate);
        const imMs = this.timerMs(detail.immunityEndDate);
        const effective = Math.max(cdMs, imMs);
        if (attackerName) {
          colorClass = 'dot-attacking';
        } else if (imMs > now) {
          colorClass    = 'dot-immune';
          cooldownClass = 'timer-active';
          cooldownText  = this.formatTimerDate(detail.immunityEndDate);
          cooldownShort = this.timerShort(imMs - now);
        } else if (cdMs > now) {
          colorClass    = 'dot-cooldown';
          cooldownClass = cdMs - now < 3600000 ? 'timer-soon' : 'timer-active';
          cooldownText  = this.formatTimerDate(detail.engagementCooldownEndDate);
          cooldownShort = this.timerShort(cdMs - now);
        } else {
          colorClass = 'dot-available';
          if (effective) {
            cooldownClass = 'timer-expired';
            cooldownText  = this.formatTimerDate(detail.engagementCooldownEndDate || detail.immunityEndDate);
          }
        }
      }
      return { id: s.id, title: s.title, svgX, svgY, colorClass, ownerName, attackerName, cooldownClass, cooldownText, cooldownShort };
    });
  }

  get galaxyMapLinks(): Array<{ x1: number; y1: number; x2: number; y2: number }> {
    const systems = Array.from(this.starSystems.values());
    if (systems.length < 2) return [];
    const nodeMap = new Map(this.galaxyMapNodes.map(n => [n.id, n]));
    const links: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
    const seen = new Set<string>();

    let hasParsed = false;
    for (const s of systems) {
      if (s.links && s.links.length > 0) { hasParsed = true; break; }
    }
    if (hasParsed) {
      for (const s of systems) {
        for (const toId of (s.links ?? [])) {
          const key = s.id < toId ? `${s.id}-${toId}` : `${toId}-${s.id}`;
          if (!seen.has(key)) {
            seen.add(key);
            const a = nodeMap.get(s.id), b = nodeMap.get(toId);
            if (a && b) links.push({ x1: a.svgX, y1: a.svgY, x2: b.svgX, y2: b.svgY });
          }
        }
      }
      return links;
    }

    let minDist = Infinity;
    for (let i = 0; i < systems.length; i++) {
      for (let j = i + 1; j < systems.length; j++) {
        const dx = +systems[i].x - +systems[j].x;
        const dy = +systems[i].y - +systems[j].y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > 0 && d < minDist) minDist = d;
      }
    }
    const threshold = minDist * 1.6;
    for (let i = 0; i < systems.length; i++) {
      for (let j = i + 1; j < systems.length; j++) {
        const dx = +systems[i].x - +systems[j].x;
        const dy = +systems[i].y - +systems[j].y;
        if (Math.sqrt(dx * dx + dy * dy) <= threshold) {
          const a = nodeMap.get(systems[i].id), b = nodeMap.get(systems[j].id);
          if (a && b) links.push({ x1: a.svgX, y1: a.svgY, x2: b.svgX, y2: b.svgY });
        }
      }
    }
    return links;
  }

  // ── Pan / zoom / touch event handlers ────────────────────────────────────

  onGalaxyMapMouseMove(e: MouseEvent): void {
    this.galaxyMapTooltipX = e.offsetX + 14;
    this.galaxyMapTooltipY = e.offsetY - 44;
    if (this.gMapPanning) {
      const dx = e.clientX - this.gMapPanStartX;
      const dy = e.clientY - this.gMapPanStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.gMapDidPan = true;
      this.galaxyMapOffsetX = this.gMapPanBaseX + dx;
      this.galaxyMapOffsetY = this.gMapPanBaseY + dy;
    }
  }

  onGalaxyMapMouseDown(e: MouseEvent): void {
    this.gMapPanning   = true;
    this.gMapDidPan    = false;
    this.gMapPanStartX = e.clientX;
    this.gMapPanStartY = e.clientY;
    this.gMapPanBaseX  = this.galaxyMapOffsetX;
    this.gMapPanBaseY  = this.galaxyMapOffsetY;
    e.preventDefault();
  }

  onGalaxyMapMouseUp(): void    { this.gMapPanning = false; }
  onGalaxyMapMouseLeave(): void { this.gMapPanning = false; this.galaxyMapHovered = null; }

  onGalaxyWheel(e: WheelEvent): void {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.18 : 1 / 1.18;
    this.galaxyMapScale = Math.min(10, Math.max(0.25, this.galaxyMapScale * factor));
  }

  onGalaxyTouchStart(e: TouchEvent): void {
    if (e.touches.length === 1) {
      this.gMapPanning   = true;
      this.gMapDidPan    = false;
      this.gMapPanStartX = e.touches[0].clientX;
      this.gMapPanStartY = e.touches[0].clientY;
      this.gMapPanBaseX  = this.galaxyMapOffsetX;
      this.gMapPanBaseY  = this.galaxyMapOffsetY;
    } else if (e.touches.length === 2) {
      this.gMapPanning = false;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      this.gMapTouchDist      = Math.sqrt(dx * dx + dy * dy);
      this.gMapTouchScaleBase = this.galaxyMapScale;
    }
  }

  onGalaxyTouchMove(e: TouchEvent): void {
    e.preventDefault();
    if (e.touches.length === 1 && this.gMapPanning) {
      const dx = e.touches[0].clientX - this.gMapPanStartX;
      const dy = e.touches[0].clientY - this.gMapPanStartY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.gMapDidPan = true;
      this.galaxyMapOffsetX = this.gMapPanBaseX + dx;
      this.galaxyMapOffsetY = this.gMapPanBaseY + dy;
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      this.galaxyMapScale = Math.min(10, Math.max(0.25, this.gMapTouchScaleBase * (dist / this.gMapTouchDist)));
    }
  }

  onGalaxyTouchEnd(e: TouchEvent): void {
    if (e.touches.length === 0) {
      this.gMapPanning = false;
    } else if (e.touches.length === 1) {
      this.gMapPanning   = true;
      this.gMapPanStartX = e.touches[0].clientX;
      this.gMapPanStartY = e.touches[0].clientY;
      this.gMapPanBaseX  = this.galaxyMapOffsetX;
      this.gMapPanBaseY  = this.galaxyMapOffsetY;
    }
  }

  onGalaxySystemTap(node: GalaxyNode): void {
    if (this.gMapDidPan) return;
    this.galaxyMapHovered = this.galaxyMapHovered?.id === node.id ? null : node;
  }
}
